import {
  ErrorMessage,
  GreyGrey10,
  GreyGrey30,
  GreyGrey70,
  GreyWhite,
  Primary13Regular,
  StandardLayout,
  Center,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { MenuManualTaskActions } from '../ManualTaskActions'
import Spinner from 'components/Loading'
import { cloneElement, isValidElement, ReactElement, ReactNode } from 'react'
import { useIsMobile } from 'hooks/useIsMobile'
import { MobileActionBar } from '../MobileActionBar/MobileActionBar'

type HeaderProps = {
  title: ReactNode
  actions?: ReactNode
  onClose: () => void
  taskId: string
}

const ManualTaskLayoutBase = (props: {
  header: ReactNode
  content: ReactNode
  // Primary actions for the task. Passed once here: rendered in the header on
  // desktop (injected into the Header element) and in a fixed bottom bar on
  // mobile, so the two placements can never fall out of sync.
  actions?: ReactNode
  'data-testid'?: string
}) => {
  const isMobile = useIsMobile()
  const showFooter = isMobile && Boolean(props.actions)

  // Feed the actions into the Header for the desktop placement so callers only
  // supply them once; Header hides them on mobile where the footer takes over.
  const header = isValidElement(props.header)
    ? cloneElement(props.header as ReactElement<HeaderProps>, {
        actions: props.actions
      })
    : props.header

  return (
    <Grid
      data-testid={props['data-testid']}
      height="100%"
      width="100%"
      overflow="hidden"
      // `minmax(0, 1fr)`, not the implicit `auto`: an auto track takes its
      // content's min-content width, so a long title or a fixed-width form
      // pane would push the modal wider than a phone screen.
      templateColumns="minmax(0, 1fr)"
      templateRows={showFooter ? 'auto 1fr auto' : 'auto 1fr'}
      gridTemplateAreas={
        showFooter ? `"header" "content" "footer"` : `"header" "content"`
      }
      backgroundColor={GreyWhite}
    >
      <GridItem gridArea="header" borderBottom={`1px solid ${GreyGrey30}`}>
        {header}
      </GridItem>
      <GridItem gridArea="content" overflow="hidden">
        {props.content}
      </GridItem>
      {showFooter && (
        <GridItem gridArea="footer">
          <MobileActionBar data-testid="manualTask.footerActions">
            {props.actions}
          </MobileActionBar>
        </GridItem>
      )}
    </Grid>
  )
}

const Header = (props: HeaderProps) => {
  // On mobile the primary actions move to a bottom bar (ManualTaskLayout's
  // actions), so the header only keeps the title, kebab menu and close.
  const isMobile = useIsMobile()

  return (
    <Grid
      width="100%"
      overflow="hidden"
      // The title track is `minmax(0, …)` so a long title truncates instead of
      // widening the header past the modal.
      templateColumns="minmax(0, 1fr) auto auto auto"
      columnGap="8px"
      height="48px"
      alignItems="center"
      gridTemplateAreas={`"title actions menu close"`}
      backgroundColor={GreyWhite}
      padding="0 16px"
    >
      <GridItem gridArea="title">{props.title}</GridItem>
      <GridItem gridArea="actions">{!isMobile && props.actions}</GridItem>
      <GridItem gridArea="menu">
        <MenuManualTaskActions taskId={props.taskId} />
      </GridItem>
      <GridItem gridArea="close">
        <StandardLayout.ButtonClose
          onClick={props.onClose}
          disableEscapeShortcut
        />
      </GridItem>
    </Grid>
  )
}

const Content = (props: {
  subject: ReactNode
  review: ReactNode
  templateColumns?: string
  // Which pane is the primary content to keep on mobile. Phones show one pane
  // at a time, so the two-pane split collapses to just this pane and the other
  // (a preview) is dropped. Defaults to the subject pane.
  mobilePrimary?: 'subject' | 'review'
}) => {
  const isMobile = useIsMobile()
  const mobilePrimary = props.mobilePrimary ?? 'subject'

  if (isMobile) {
    const primaryPane =
      mobilePrimary === 'subject' ? props.subject : props.review

    return (
      <Box
        data-testid="manualTask.content.mobile"
        height="100%"
        width="100%"
        overflow="hidden"
        backgroundColor={GreyWhite}
      >
        {primaryPane}
      </Box>
    )
  }

  return (
    <Grid
      height="100%"
      width="100%"
      overflow="hidden"
      templateColumns={props.templateColumns ?? '1fr auto'}
      gridTemplateAreas={`"subject review"`}
    >
      <GridItem
        gridArea="subject"
        data-testid="manualTask.content.subject"
        backgroundColor={GreyWhite}
        borderRight={`1px solid ${GreyGrey30}`}
        overflow="hidden"
        height="100%"
      >
        {props.subject}
      </GridItem>
      <GridItem
        gridArea="review"
        data-testid="manualTask.content.review"
        backgroundColor={GreyWhite}
        overflow="hidden"
        height="100%"
      >
        {props.review}
      </GridItem>
    </Grid>
  )
}

const Loading = () => {
  return (
    <Center
      height="100%"
      width="100%"
      overflow="hidden"
      backgroundColor={GreyGrey10}
    >
      <Flex flexDirection="column" gap="8px">
        <Spinner color={GreyGrey70} />
        <Box {...Primary13Regular} color={GreyGrey70}>
          Getting your task ready...
        </Box>
      </Flex>
    </Center>
  )
}

const TaskError = () => {
  return <ErrorMessage title="We couldn't load your task" />
}

const Actions = (props: { children: React.ReactNode }) => {
  return <Flex gap="8px">{props.children}</Flex>
}

const ReviewFooter = (props: { actions: React.ReactNode }) => {
  return (
    <Grid
      padding="0 16px"
      height="48px"
      gridTemplateColumns="1fr auto"
      borderTop={`1px solid ${GreyGrey30}`}
      alignItems="center"
      backgroundColor={GreyWhite}
    >
      <Box></Box>
      <Box>{props.actions}</Box>
    </Grid>
  )
}

const ReviewBase = (props: { form: ReactNode; footer: ReactNode }) => {
  return (
    <Grid
      height="100%"
      width="100%"
      overflow="hidden"
      templateRows="1fr auto"
      gridTemplateAreas={`
    "form"
    "actions"
 `}
      backgroundColor={GreyGrey10}
    >
      <GridItem gridArea="form">{props.form}</GridItem>
      <GridItem gridArea="actions">{props.footer}</GridItem>
    </Grid>
  )
}

const ReviewForm = (props: { children: React.ReactNode }) => {
  return (
    <Box padding="20px" backgroundColor={GreyWhite} height="100%">
      {props.children}
    </Box>
  )
}

const Review = Object.assign(ReviewBase, {
  Footer: ReviewFooter,
  Actions,
  Form: ReviewForm
})

export const ManualTaskLayout = Object.assign(ManualTaskLayoutBase, {
  Header,
  Content,
  Loading,
  Error: TaskError,
  Actions,
  Review
})
